import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckInScanner } from '../components/CheckInScanner';
import {
  ScanLine,
  Lock,
  KeyRound,
  Mail,
  LogOut,
  ArrowLeft,
  ShieldCheck,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { eventInfo } from '../data';
import { getSession, signInWithEmailPassword, signOut } from '../lib/supabase';

export function CheckInPage() {
  const [adminKey, setAdminKey] = useState<string>(() => {
    try {
      return sessionStorage.getItem('iiab_admin_key') || '';
    } catch {
      return '';
    }
  });
  const [isCheckingSession, setIsCheckingSession] = useState<boolean>(!adminKey);
  const [loginMode, setLoginMode] = useState<'pin' | 'email'>('pin');
  const [pinInput, setPinInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function checkExistingSession() {
      try {
        const storedKey = sessionStorage.getItem('iiab_admin_key');
        if (storedKey) {
          if (mounted) {
            setAdminKey(storedKey);
            setIsCheckingSession(false);
          }
          return;
        }
        const session = await getSession();
        if (session?.access_token && mounted) {
          sessionStorage.setItem('iiab_admin_key', session.access_token);
          setAdminKey(session.access_token);
        }
      } catch {
        // Ignore session check errors
      } finally {
        if (mounted) setIsCheckingSession(false);
      }
    }
    checkExistingSession();
    return () => {
      mounted = false;
    };
  }, []);

  const handlePinLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const key = pinInput.trim();
    if (!key) {
      setAuthError('Please enter a valid Scanner PIN or Staff Passkey.');
      return;
    }
    setAuthError(null);
    sessionStorage.setItem('iiab_admin_key', key);
    setAdminKey(key);
    setPinInput('');
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim() || !passwordInput) {
      setAuthError('Please enter both staff email and password.');
      return;
    }
    setIsSubmitting(true);
    setAuthError(null);
    try {
      const { session, error } = await signInWithEmailPassword(
        emailInput.trim(),
        passwordInput
      );
      if (error || !session?.access_token) {
        setAuthError(typeof error === 'string' ? error : (error as any)?.message || 'Invalid staff credentials.');
      } else {
        sessionStorage.setItem('iiab_admin_key', session.access_token);
        setAdminKey(session.access_token);
        setPasswordInput('');
      }
    } catch (err: any) {
      setAuthError(err.message || 'Authentication failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    try {
      sessionStorage.removeItem('iiab_admin_key');
      await signOut();
    } catch {
      // ignore
    }
    setAdminKey('');
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex items-center gap-2.5 text-slate-600 text-sm font-semibold">
          <RefreshCw className="w-4 h-4 animate-spin text-[#0A3D27]" />
          <span>Verifying Gate Kiosk session...</span>
        </div>
      </div>
    );
  }

  if (!adminKey) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-br from-[#0A3D27] to-[#062B1B] p-6 text-white space-y-2">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#E6C687] text-[#0A3D27] text-[10px] font-black uppercase tracking-wider">
              <ScanLine className="w-3 h-3" />
              <span>Pavilion 3 • Gate Turnstile Terminal</span>
            </div>
            <h1 className="text-xl font-black tracking-tight">
              IIAB 2027 Staff & Gate Kiosk Login
            </h1>
            <p className="text-xs text-white/80 leading-relaxed">
              Authenticate with your Scanner Passkey PIN or Staff Account to unlock the Turnstile QR Scanner for {eventInfo.title}.
            </p>
          </div>

          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-100 rounded-xl text-xs font-bold">
              <button
                type="button"
                onClick={() => {
                  setLoginMode('pin');
                  setAuthError(null);
                }}
                className={`py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  loginMode === 'pin'
                    ? 'bg-white text-[#0A3D27] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <KeyRound className="w-3.5 h-3.5" />
                <span>Scanner / Staff PIN</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginMode('email');
                  setAuthError(null);
                }}
                className={`py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  loginMode === 'email'
                    ? 'bg-white text-[#0A3D27] shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Mail className="w-3.5 h-3.5" />
                <span>Staff Email Login</span>
              </button>
            </div>

            {authError && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            {loginMode === 'pin' ? (
              <form onSubmit={handlePinLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-slate-700">
                    Scanner Passkey / Admin PIN
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="password"
                      value={pinInput}
                      onChange={(e) => setPinInput(e.target.value)}
                      placeholder="Enter scanner passkey or staff key..."
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-hidden focus:border-[#0A3D27] focus:bg-white"
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  <ShieldCheck className="w-4 h-4 text-[#E6C687]" />
                  <span>Unlock Gate Turnstile Kiosk</span>
                </button>
              </form>
            ) : (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-slate-700">
                    Staff Email
                  </label>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="ops@iiabexpo.com"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-hidden focus:border-[#0A3D27] focus:bg-white"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-slate-700">
                    Password
                  </label>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-hidden focus:border-[#0A3D27] focus:bg-white"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                >
                  {isSubmitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 text-[#E6C687]" />
                  )}
                  <span>Sign In & Unlock Kiosk</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Compact Kiosk Top Bar */}
      <div className="bg-[#062B1B] text-white rounded-2xl px-4 py-3 flex flex-wrap items-center justify-between gap-3 shadow-sm border border-[#0A3D27]">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E6C687] text-[#0A3D27] text-[10px] font-black uppercase tracking-wider">
            <ScanLine className="w-3 h-3" />
            <span>Active Station • Gate 1</span>
          </span>
          <span className="text-xs sm:text-sm font-black tracking-wide text-[#FAF5EA]">
            IIAB 2027 • GATE TURNSTILE KIOSK
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/admin/operations"
            className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-colors flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-[#E6C687]" />
            <span>← Live Ops Console</span>
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="px-3 py-1.5 rounded-xl bg-rose-600/90 hover:bg-rose-600 text-white text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Lock / Sign Out</span>
          </button>
        </div>
      </div>

      <CheckInScanner adminKey={adminKey} />
    </div>
  );
}

export default CheckInPage;
